/**
 * AntifraudPage — `/antifraud`. Сверка форм Директа (MQL) со сделками Bitrix
 * (воронка КОММА): качество, воронка NQL→Won, матч по кампаниям, покрытие UTM.
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { PeriodPicker, periodLabel, type Period } from '@/components/ui/PeriodPicker';
import { Badge, Button, LoadingBlock, ErrorBlock, Empty } from '@/shared/ui';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useAntifraud } from '@/hooks/api/useAntifraud';

function rub(n: number): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);
}

export function AntifraudPage() {
  usePageHeader({ title: 'Антифрод / CRM', subtitle: 'Формы Директа ↔ сделки Bitrix (воронка КОММА)' });

  const [period, setPeriod] = useState<Period>({ days: 30 });
  const { data, isLoading, isError, error, refetch } = useAntifraud(period);
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();

  const ask = () => {
    openPanel();
    void sendMessage(
      `Сверь формы Директа со сделками Bitrix (воронка КОММА, cat 3) за ${periodLabel(period)} через скилл bitrix-crm: ` +
        `есть ли признаки фрода (формы без сделок), как идёт NQL→MQL→SQL→Won, и какие кампании дают качественные сделки.`,
    );
  };

  return (
    <div className="col gap20" style={{ maxWidth: 1100 }}>
      <div className="row spread">
        <Button variant="outline" size="sm" icon="Sparkles" onClick={ask}>Разбор через AI</Button>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {isLoading && <LoadingBlock label="Сверяю Директ с CRM…" />}
      {isError && <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />}
      {data && !data.configured && (
        <Empty icon="Plug" title="CRM не подключена" text="Нужен BITRIX_WEBHOOK_URL и YANDEX_DIRECT_TOKEN на сервере." />
      )}

      {data?.configured && data.crm && data.direct && data.quality && (
        <>
          <div className="stat-grid">
            <Stat label="Формы Директа (все цели)" value={String(data.direct.forms)} hint={`komma+call: ${data.direct.formsKomma}`} />
            <Stat label="Сделки в CRM" value={String(data.crm.dealsCreated)} hint={`из рекламы: ${data.crm.fromDirect}`} />
            <Stat label="Won" value={`${data.crm.won.count} · ${rub(data.crm.won.sum)}`} tone="success" />
            <Stat label="Покрытие UTM" value={`${data.crm.utmCoverage.pct}%`}
              tone={data.crm.utmCoverage.pct >= 80 ? 'success' : 'danger'}
              hint={`${data.crm.utmCoverage.withUtm}/${data.crm.utmCoverage.total} сделок`} />
          </div>

          <section className="card col" style={{ gap: 10 }}>
            <div className="row center gap8">
              <h3 style={{ margin: 0 }}>Реклама → сделка</h3>
              <Badge tone={(data.quality.formsToDeals ?? 0) >= 0.5 ? 'b-success' : 'b-warning'}>
                {data.quality.formsToDeals != null ? Math.round((data.quality.formsToDeals) * 100) + '%' : '—'}
              </Badge>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Доля = сделки с рекламным utm_source ({data.crm.fromDirect}) ÷ все формы Директа ({data.direct.forms}).
              Это реальная конверсия «форма → сделка» для рекламного трафика. Lost: {data.crm.lost}.
              {data.crm.utmCoverage.pct < 80 && ' UTM проброшен не везде → часть рекламных сделок не помечена (см. ТЗ на UTM-проброс).'}
            </p>
          </section>

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Воронка КОММА</h3>
            <table className="tbl">
              <tbody>
                {data.crm.funnel.map((s) => (
                  <tr key={s.stageId}>
                    <td>{s.name}</td>
                    <td className="tnum" style={{ textAlign: 'right', width: 80, fontWeight: 700 }}>{s.count}</td>
                    <td style={{ width: '60%' }}>
                      <div style={{
                        height: 10, borderRadius: 5, background: 'var(--primary)',
                        width: `${Math.min(100, (s.count / Math.max(1, data.crm!.dealsCreated)) * 100)}%`,
                        opacity: s.stageId.endsWith(':WON') ? 1 : s.stageId.endsWith(':LOSE') ? 0.35 : 0.7,
                      }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card">
            <h3 style={{ marginTop: 0 }}>По кампаниям (где есть UTM)</h3>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Кампания</th>
                  <th style={{ textAlign: 'right' }}>Формы Директ</th>
                  <th style={{ textAlign: 'right' }}>Сделки CRM</th>
                  <th style={{ textAlign: 'right' }}>Won</th>
                </tr>
              </thead>
              <tbody>
                {data.perCampaign?.map((p, i) => (
                  <tr key={`${p.campaign}-${i}`}>
                    <td>{p.campaign}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{p.directForms ?? '—'}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{p.crmDeals}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontWeight: p.won ? 700 : 400, color: p.won ? 'var(--success-fg)' : undefined }}>
                      {p.won || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
              komma/call часто теряют utm_campaign на лендинге → там матч агрегатный. Чинится на лендинге (проброс UTM в форму).
            </p>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'neutral', hint }: {
  label: string; value: string; tone?: 'success' | 'danger' | 'neutral'; hint?: string;
}) {
  const color = tone === 'success' ? 'var(--success-fg)' : tone === 'danger' ? 'var(--danger-fg)' : 'var(--text-muted)';
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      {hint && <div className="stat__delta" style={{ color }}>{hint}</div>}
    </div>
  );
}
